import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const browserPositionKeys = ['vspots_geo_position_v3', 'vspots_geo_position_v2'];
  const manualLocationKey = 'vspots_geo_manual_location_v2';
  const positionLifetime = 7 * 24 * 60 * 60 * 1000;
  const manualLocationLifetime = 30 * 24 * 60 * 60 * 1000;
  const recommendationCacheLifetime = 5 * 60 * 1000;
  const recommendationRequestTimeout = 8000;
  const mobileMaximumAccuracy = 10000;
  const desktopMaximumAccuracy = 30000;
  const highAccuracyRequestTimeout = 20000;
  const networkRequestTimeout = 15000;
  const recommendationMemoryCache = new Map();

  function maximumAcceptedAccuracy() {
    return window.matchMedia('(pointer: fine)').matches ? desktopMaximumAccuracy : mobileMaximumAccuracy;
  }

  function parseStoredValue(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key));
    } catch {
      window.localStorage.removeItem(key);
      return null;
    }
  }

  function readLocation() {
    const selection = parseStoredValue('vspots_city_selection_v1');
    const urlCity = new URL(window.location.href).searchParams.get('city');
    const city = (urlCity ?? selection?.city ?? '').trim().slice(0, 80);
    if (city && (urlCity !== null || selection?.source === 'manual')) {
      return { city, label: city };
    }
    for (const key of browserPositionKeys) {
      const position = parseStoredValue(key);
      const isCurrent =
        Number.isFinite(position?.latitude) &&
        Number.isFinite(position?.longitude) &&
        (!Number.isFinite(position?.accuracy) || position.accuracy <= maximumAcceptedAccuracy()) &&
        (!Number.isFinite(position?.createdAt) || Date.now() - position.createdAt < positionLifetime);
      if (isCurrent) {
        return {
          latitude: position.latitude,
          longitude: position.longitude,
          label: '',
        };
      }
    }

    const manualLocation = parseStoredValue(manualLocationKey);
    const isCurrentManualLocation =
      Number.isFinite(manualLocation?.latitude) &&
      Number.isFinite(manualLocation?.longitude) &&
      Number.isFinite(manualLocation?.createdAt) &&
      Date.now() - manualLocation.createdAt < manualLocationLifetime;

    return isCurrentManualLocation
      ? {
          latitude: manualLocation.latitude,
          longitude: manualLocation.longitude,
          label: String(manualLocation.label || manualLocation.value || ''),
        }
      : city ? { city, label: city } : {};
  }

  function saveBrowserPosition(position) {
    try {
      window.localStorage.setItem(
        browserPositionKeys[0],
        JSON.stringify({
          latitude: Number(position.latitude.toFixed(5)),
          longitude: Number(position.longitude.toFixed(5)),
          accuracy: Math.round(position.accuracy),
          createdAt: Date.now(),
        })
      );
      window.localStorage.removeItem(manualLocationKey);
    } catch {
      return;
    }
  }

  function formatMoney(currency, cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format((Number(cents) || 0) / 100);
  }

  function formatDistance(meters) {
    if (!Number.isFinite(meters)) return '';
    if (meters < 1000) return `${Math.max(100, Math.round(meters / 100) * 100)} m`;

    const kilometers = meters / 1000;
    return `${new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      maximumFractionDigits: kilometers < 10 ? 1 : 0,
    }).format(kilometers)} km`;
  }

  function productNumericId(value) {
    return String(value || '').split('/').pop();
  }

  function recommendationCacheKey(location) {
    return `vspots_cart_nearby_v2:${location.city || ''}:${Number.isFinite(location.latitude) ? location.latitude.toFixed(3) : ''}:${Number.isFinite(location.longitude) ? location.longitude.toFixed(3) : ''}`;
  }

  function readRecommendationsCache(location) {
    const key = recommendationCacheKey(location);
    const memoryCached = recommendationMemoryCache.get(key);
    if (
      Array.isArray(memoryCached?.results) &&
      Number.isFinite(memoryCached?.createdAt) &&
      Date.now() - memoryCached.createdAt < recommendationCacheLifetime
    ) {
      return memoryCached.results;
    }

    try {
      const cached = JSON.parse(window.sessionStorage.getItem(key));
      const isCurrent =
        Array.isArray(cached?.results) &&
        Number.isFinite(cached?.createdAt) &&
        Date.now() - cached.createdAt < recommendationCacheLifetime;
      if (isCurrent) recommendationMemoryCache.set(key, cached);
      return isCurrent ? cached.results : null;
    } catch {
      return null;
    }
  }

  function saveRecommendationsCache(location, results) {
    const key = recommendationCacheKey(location);
    const cacheEntry = { results, createdAt: Date.now() };
    recommendationMemoryCache.set(key, cacheEntry);

    try {
      window.sessionStorage.setItem(key, JSON.stringify(cacheEntry));
    } catch {
      return;
    }
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  async function getCheapestAvailableVariant(productPath, signal) {
    const response = await window.fetch(`${productPath}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) throw new Error(`Product endpoint returned ${response.status}`);

    const product = await response.json();
    return (product.variants || [])
      .filter((variant) => variant.available)
      .sort((left, right) => Number(left.price) - Number(right.price))[0];
  }

  class VspotsCartRecommendations extends HTMLElement {
    request = null;

    addRequests = new Set();

    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.querySelector('[data-vspots-cart-location-trigger]')?.addEventListener('click', () => {
        this.requestLocation();
      });
      if (this.closest('theme-drawer')?.hasAttribute('open')) this.load();
    }

    setStatus(message, state = 'loading') {
      const status = this.querySelector('[data-vspots-cart-recommendations-status]');
      const grid = this.querySelector('[data-vspots-cart-recommendations-grid]');
      this.dataset.state = state;
      if (status) {
        status.textContent = message;
        status.hidden = message === '';
      }
      grid?.setAttribute('aria-busy', String(state === 'loading'));
    }

    showLocationButton(show) {
      const button = this.querySelector('[data-vspots-cart-location-trigger]');
      if (button) button.hidden = !show;
    }

    async requestLocation() {
      if (!window.navigator.geolocation) {
        this.setStatus(this.dataset.errorLabel || '', 'error');
        return;
      }

      this.showLocationButton(false);
      this.setStatus(this.dataset.loadingLabel || '', 'loading');
      let isFinished = false;

      const finishWithError = () => {
        if (isFinished) return;
        isFinished = true;
        this.setStatus(this.dataset.errorLabel || '', 'error');
        this.showLocationButton(true);
      };

      const usePosition = ({ coords }) => {
        if (isFinished) return;
        const position = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
        };
        const isValid =
          Number.isFinite(position.latitude) &&
          Number.isFinite(position.longitude) &&
          Number.isFinite(position.accuracy);

        if (isValid && position.accuracy <= maximumAcceptedAccuracy()) {
          isFinished = true;
          saveBrowserPosition(position);
          document.dispatchEvent(new CustomEvent('vspots:location-change', { detail: position }));
          window.dispatchEvent(new CustomEvent('vspots:location-changed', { detail: position }));
          this.load(position);
          return true;
        }

        return false;
      };

      const requestHighAccuracyPosition = () => {
        if (isFinished) return;
        window.navigator.geolocation.getCurrentPosition(
          (position) => {
            if (!usePosition(position)) finishWithError();
          },
          finishWithError,
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: highAccuracyRequestTimeout,
          }
        );
      };

      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!usePosition(position)) requestHighAccuracyPosition();
        },
        requestHighAccuracyPosition,
        {
          enableHighAccuracy: false,
          maximumAge: 60 * 60 * 1000,
          timeout: networkRequestTimeout,
        }
      );
    }

    buildSearchUrl(location) {
      const baseUrl = String(this.dataset.searchApiUrl || '').trim();
      const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const url = new URL('v1/deals/search', normalizedBaseUrl);
      url.searchParams.set('q', '');
      url.searchParams.set('page', '1');
      url.searchParams.set('perPage', '12');
      const hasCoordinates = Number.isFinite(location.latitude) && Number.isFinite(location.longitude);
      url.searchParams.set('sort', hasCoordinates ? 'nearby' : 'relevance');
      if (hasCoordinates) {
        url.searchParams.set('lat', String(location.latitude));
        url.searchParams.set('lng', String(location.longitude));
      }
      if (location.city) url.searchParams.set('city', location.city);
      return url;
    }

    async load(forcedLocation = null) {
      const location = forcedLocation || readLocation();
      const hasLocation = Boolean(location.city) || Number.isFinite(location.latitude);
      this.showLocationButton(!hasLocation);
      const heading = this.querySelector('.vspots-cart-recommendations__heading h3');
      if (heading) heading.textContent = hasLocation ? this.dataset.nearbyHeading : this.dataset.generalHeading;
      const label = this.querySelector('[data-vspots-cart-location-label]');
      if (label) {
        label.textContent = location.label || '';
        label.hidden = !location.label;
      }
      const cachedResults = readRecommendationsCache(location);
      if (cachedResults) {
        this.render(cachedResults);
        return;
      }

      this.setStatus(this.dataset.loadingLabel || '', 'loading');

      let activeRequest = null;
      let requestTimeout = null;
      let requestTimedOut = false;

      try {
        this.request?.abort();
        activeRequest = new AbortController();
        this.request = activeRequest;
        requestTimeout = window.setTimeout(() => {
          requestTimedOut = true;
          activeRequest.abort();
        }, recommendationRequestTimeout);
        const response = await window.fetch(this.buildSearchUrl(location), {
          headers: { Accept: 'application/json' },
          mode: 'cors',
          signal: activeRequest.signal,
        });
        window.clearTimeout(requestTimeout);
        if (this.request !== activeRequest) return;
        if (!response.ok) throw new Error(`Search API returned ${response.status}`);
        const body = await response.json();
        const results = body.results || [];
        saveRecommendationsCache(location, results);
        this.render(results);
      } catch (error) {
        if (error.name === 'AbortError' && !requestTimedOut) return;
        this.setStatus(this.dataset.errorLabel || '', 'error');
      } finally {
        if (requestTimeout !== null) window.clearTimeout(requestTimeout);
      }
    }

    render(results) {
      const grid = this.querySelector('[data-vspots-cart-recommendations-grid]');
      if (!grid) return;

      const excludedIds = new Set(
        String(this.dataset.cartProductIds || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      );
      const recommendations = results
        .filter(({ deal }) => deal?.available !== false && !excludedIds.has(productNumericId(deal?.shopify_product_id)))
        .slice(0, Number(this.dataset.limit) || 2);

      grid.replaceChildren();
      if (recommendations.length === 0) {
        this.setStatus(this.dataset.emptyLabel || '', 'empty');
        return;
      }

      const fragment = document.createDocumentFragment();
      recommendations.forEach(({ deal, distance_meters: distanceMeters }) => {
        const card = createElement('article', 'vspots-cart-recommendation');

        const media = createElement('a', 'vspots-cart-recommendation__media');
        media.href = deal.product_path;
        media.setAttribute('aria-label', deal.title);
        if (deal.image_url) {
          const image = document.createElement('img');
          image.src = deal.image_url;
          image.alt = deal.image_alt || deal.title;
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        }
        const distance = formatDistance(distanceMeters);
        if (distance) media.append(createElement('span', 'vspots-cart-recommendation__distance', distance));

        const content = createElement('span', 'vspots-cart-recommendation__content');
        const title = createElement('a', 'vspots-cart-recommendation__title', deal.title);
        title.href = deal.product_path;
        content.append(title);
        const details = [deal.vendor, ...(deal.cities || [])].filter(Boolean).join(' · ');
        if (details) content.append(createElement('span', 'vspots-cart-recommendation__meta', details));
        const price = createElement('span', 'vspots-cart-recommendation__price');
        const offerPrice = Number(deal.min_price_cents) || 0;
        const comparePrice = Number((deal.display_compare_at_price_cents ?? (Number(deal.variant_count) === 1 ? deal.max_compare_at_price_cents : 0))) || 0;
        if (comparePrice > offerPrice) {
          const discountPercent = Math.round(((comparePrice - offerPrice) * 100) / comparePrice);
          const discountLabel = String(this.dataset.discountLabel || '[percent] % Rabatt').replace(
            '[percent]',
            String(discountPercent)
          );
          price.append(
            createElement(
              's',
              'vspots-cart-recommendation__price-compare',
              formatMoney(this.dataset.currency, comparePrice)
            ),
            createElement(
              'strong',
              'vspots-cart-recommendation__price-offer',
              formatMoney(this.dataset.currency, offerPrice)
            ),
            createElement('span', 'vspots-cart-recommendation__discount', discountLabel)
          );
        } else {
          price.append(
            createElement(
              'strong',
              'vspots-cart-recommendation__price-offer vspots-cart-recommendation__price-offer--regular',
              formatMoney(this.dataset.currency, offerPrice)
            )
          );
        }
        content.append(price);

        const action = createElement('button', 'vspots-cart-recommendation__action');
        action.type = 'button';
        action.setAttribute('aria-label', `${this.dataset.addLabel}: ${deal.title}`);
        action.textContent = '+';
        action.addEventListener('click', () => this.addDeal(deal, action));
        card.append(media, content, action);
        fragment.append(card);
      });

      grid.append(fragment);
      this.setStatus('', 'ready');
    }

    async addDeal(deal, button) {
      if (button.disabled) return;

      const request = new AbortController();
      this.addRequests.add(request);
      button.disabled = true;
      button.dataset.state = 'adding';
      button.textContent = '…';
      button.setAttribute('aria-label', `${this.dataset.addingLabel}: ${deal.title}`);

      let deferredEvent = null;

      try {
        const variant = await getCheapestAvailableVariant(deal.product_path, request.signal);
        if (!variant?.id) throw new Error('No available variant found');

        deferredEvent = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
          promise: deferredEvent.promise,
          })
        );

        const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((component) => component.dataset.sectionId)
          .filter(Boolean);
        const addResponse = await window.fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          signal: request.signal,
          body: JSON.stringify({
            items: [{ id: Number(variant.id), quantity: 1 }],
            sections: [...new Set(sectionIds)].join(','),
            sections_url: window.location.pathname,
          }),
        });
        const addResult = await addResponse.json();
        if (!addResponse.ok || addResult.status) {
          throw new Error(addResult.description || addResult.message || 'Add to cart failed');
        }

        const cartResponse = await window.fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: request.signal,
        });
        if (!cartResponse.ok) throw new Error(`Cart endpoint returned ${cartResponse.status}`);
        const cart = await cartResponse.json();

        button.dataset.state = 'added';
        button.textContent = '✓';
        button.setAttribute('aria-label', `${this.dataset.addedLabel}: ${deal.title}`);
        deferredEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'vspots-cart-recommendations',
            sourceId: productNumericId(deal.shopify_product_id),
            itemCount: 1,
            productId: deal.shopify_product_id,
            sections: addResult.sections,
            didError: false,
          },
        });
      } catch (error) {
        if (error.name === 'AbortError') {
          deferredEvent?.reject(error);
          return;
        }

        deferredEvent?.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || this.dataset.addErrorLabel,
            code: 'INVALID',
          })
        );
        button.disabled = false;
        button.dataset.state = 'error';
        button.textContent = '!';
        button.setAttribute('aria-label', `${this.dataset.addErrorLabel}: ${deal.title}`);
        this.setStatus(this.dataset.addErrorLabel || '', 'error');
      } finally {
        this.addRequests.delete(request);
      }
    }
  }

  if (!customElements.get('vspots-cart-recommendations')) {
    customElements.define('vspots-cart-recommendations', VspotsCartRecommendations);
  }
})();
